/**
 * @fileoverview API Configuration Module
 *
 * Centralized configuration for all API instances in the agnostic-platform.
 * This module is part of the apiService decomposition and provides:
 * - Type definitions for API configuration
 * - Environment variable validation
 * - API endpoint configuration constants
 * - Cache settings
 *
 * @module services/api/config
 */

import { AxiosRequestConfig, AxiosResponse } from 'axios';

import { API_URLS } from 'utils/apiUrlBuilder';
import { logger } from 'utils/logger';

// ============================================================================
// Types and Interfaces
// ============================================================================

/**
 * Configuration object for creating API instances
 *
 * @interface ApiConfig
 * @property {string | undefined} baseURL - The base URL for all requests made by this instance
 * @property {Record<string, string>} [headers] - Default headers to include in every request
 */
export interface ApiConfig {
  baseURL: string | undefined;
  headers?: Record<string, string>;
}

/**
 * Standard HTTP methods interface implemented by ApiWrapper
 *
 * Provides type-safe HTTP method signatures with generic response types.
 * All methods return Promises that resolve to AxiosResponse objects.
 *
 * @interface ApiMethods
 * @template T - The expected response data type (defaults to unknown)
 */
export interface ApiMethods {
  /**
   * Performs a GET request
   * @template T - Response data type
   * @param url - Request URL (relative to baseURL)
   * @param config - Optional Axios request config
   * @returns Promise resolving to AxiosResponse
   */
  get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;

  /**
   * Performs a POST request
   * @template T - Response data type
   * @param url - Request URL (relative to baseURL)
   * @param data - Request body data
   * @param config - Optional Axios request config
   * @returns Promise resolving to AxiosResponse
   */
  post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>>;

  /**
   * Performs a PUT request
   * @template T - Response data type
   * @param url - Request URL (relative to baseURL)
   * @param data - Request body data
   * @param config - Optional Axios request config
   * @returns Promise resolving to AxiosResponse
   */
  put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>>;

  /**
   * Performs a PATCH request
   * @template T - Response data type
   * @param url - Request URL (relative to baseURL)
   * @param data - Request body data
   * @param config - Optional Axios request config
   * @returns Promise resolving to AxiosResponse
   */
  patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>>;

  /**
   * Performs a DELETE request
   * @template T - Response data type
   * @param url - Request URL (relative to baseURL)
   * @param config - Optional Axios request config
   * @returns Promise resolving to AxiosResponse
   */
  delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
}

// ============================================================================
// Environment Validation
// ============================================================================

/**
 * Validates that all required environment variables are defined
 *
 * Called automatically on module initialization to ensure the application
 * has all necessary configuration. Logs errors for any missing variables
 * but does not throw to allow graceful degradation.
 *
 * Required environment variables:
 * - `REACT_APP_BASE_URL` - Main API base URL
 * - `REACT_APP_SERVICES_BASE_URL` - Services API base URL
 * - `REACT_APP_CAND_URL` - CAND Core API URL
 * - `REACT_APP_STRAPI_BASE_URL` - Strapi CMS API URL
 * - `REACT_APP_URL_AWS_COGNITO_USER_POOL` - AWS Cognito User Pool ID
 *
 * @returns {void}
 */
export const validateEnvVariables = (): void => {
  const requiredEnvVars = [
    'REACT_APP_BASE_URL',
    'REACT_APP_SERVICES_BASE_URL',
    'REACT_APP_CAND_URL',
    'REACT_APP_STRAPI_BASE_URL',
    'REACT_APP_URL_AWS_COGNITO_USER_POOL',
  ];

  const missingVars = requiredEnvVars.filter(envVar => !process.env[envVar]);

  if (missingVars.length > 0) {
    logger.error('Missing required environment variables:', missingVars.join(', '));
  }
};

// ============================================================================
// API Configuration
// ============================================================================

/**
 * Centralized configuration object mapping API keys to their base URLs
 *
 * Uses environment variables and API_URLS utility for URL resolution.
 * Each key corresponds to an exported API instance.
 *
 * @constant
 * @type {Object}
 * @property {string} main - Main application API (REACT_APP_BASE_URL)
 * @property {string} user - User/CAND Core API
 * @property {string} rainmakersV2 - Rainmakers V2 API
 * @property {string} advisor - Advisor service API
 * @property {string} meeting - Meeting/video service API
 * @property {string} orchestrator - Authentication orchestrator API (with cookies)
 * @property {string} copilot - AI Copilot service API
 * @property {string} networking - Networking features API
 * @property {string} pbi - Power BI integration API
 * @property {string} forms - Dynamic forms API
 * @property {string} notifications - Notifications service API
 * @property {string} strapi - Strapi CMS API
 */
export const API_CONFIG = {
  // Core APIs
  main: process.env.REACT_APP_BASE_URL,
  user: API_URLS.CAND_CORE_API,
  rainmakersV2: process.env.REACT_APP_BASE_URL,

  // Platform APIs
  advisor: API_URLS.ADVISOR,
  meeting: API_URLS.MEETING,
  orchestrator: API_URLS.ORCHESTRATOR_POLICY_V2,

  // External Services
  copilot: API_URLS.COPILOT,
  networking: API_URLS.NETWORKING_API,
  pbi: API_URLS.PBI,
  forms: API_URLS.FORMS,
  notifications: API_URLS.NOTIFICATIONS_API,

  // CMS APIs
  strapi: API_URLS.STRAPI_API,
} as const;

/**
 * Type representing valid API configuration keys
 * Derived from the API_CONFIG constant
 */
export type ApiKey = keyof typeof API_CONFIG;

// ============================================================================
// Cache Configuration
// ============================================================================

/**
 * Time-to-live for header cache in milliseconds
 * Headers are cached for 1 minute to minimize localStorage reads
 *
 * @constant
 */
export const CACHE_TTL = 60000; // 1 minute header cache TTL

/**
 * List of API instance keys that should have withCredentials enabled
 * These instances send cookies (access_token, refresh_token) with requests
 *
 * IMPORTANT: Instances with withCredentials: true require the backend to have
 * correct CORS headers configured:
 * - Access-Control-Allow-Credentials: true
 * - Access-Control-Allow-Origin: <specific origin> (NOT *)
 *
 * @constant
 * @updated 2026-02-05 - Added all API instances to support cookie-based authentication
 */
export const INSTANCES_WITH_CREDENTIALS = [
  'orchestrator',
  'main',
  'user',
  'rainmakersV2',
  'advisor',
  'meeting',
  'copilot',
  'networking',
  'pbi',
  'forms',
  'notifications',
  'strapi',
] as const;

/**
 * Type for instances that use credentials
 */
export type InstanceWithCredentials = (typeof INSTANCES_WITH_CREDENTIALS)[number];
